/**
 * Leave Confirmation Modal
 * Modal to confirm leaving the preview screen using shadcn Dialog
 */

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/language"

interface LeaveConfirmationModalProps {
  isOpen: boolean
  onKeepEditing: () => void
  onLeave: () => void
  /** Optional overrides; default to generic "unsaved changes / upload in
   *  progress" copy so the modal reads correctly on any form page. */
  title?: string
  description?: string
}

export function LeaveConfirmationModal({
  isOpen,
  onKeepEditing,
  onLeave,
  title,
  description,
}: LeaveConfirmationModalProps) {
  const { t } = useTranslation("common")

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onKeepEditing()}>
      <DialogContent
        showCloseButton={true}
        className="w-lg bg-brand-terracotta-bright/10 border-red/20 p-15"
      >
        <DialogHeader className="text-center">
          <DialogTitle className="text-primary text-2xl font-semibold text-center">
            {title ?? t("leaveConfirmation.title")}
          </DialogTitle>
          <DialogDescription className="text-primary text-center text-sm leading-relaxed">
            {description ?? t("leaveConfirmation.description")}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <div className="flex flex-col gap-3 h-full w-full">
            <Button
              variant="primary-white"
              onClick={onKeepEditing}
              className="flex-1 px-20 py-4 uppercase"
            >
              {t("leaveConfirmation.keepEditing")}
            </Button>
            <Button variant="outline" onClick={onLeave} className="flex-1 px-20 py-4 uppercase">
              {t("leaveConfirmation.leaveAnyway")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
